import { useEffect, useState, useCallback } from "react";
import { listAdminArchive } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

const PAGE_SIZE = 50;

function shortId(id) {
  return id ? "C-" + id.slice(0, 6).toUpperCase() : "";
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months =
    (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

export default function AdminArchive({ onSignOut }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 300ms 디바운스 — 타이핑 중에 매 키마다 요청 보내지 않도록.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // 검색어 바뀌면 페이지 리셋
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { rows, total } = await listAdminArchive({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: debouncedSearch,
      });
      setRows(rows);
      setTotal(total);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total && rows.length === PAGE_SIZE;

  return (
    <div className="admin">
      <AdminSidebar active="archive" onSignOut={onSignOut}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 className="admin-h1">📚 리포트 아카이브</h1>
            <p className="admin-sub">
              발행 완료된 리포트를 다시 열어볼 수 있어요. 전체 {total}건.
            </p>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 아이 이름 / 보호자 이메일 / 증상"
              style={{
                padding:'8px 14px',fontSize:13,borderRadius:9999,
                border:'1px solid #e2e8f0',background:'#fff',
                fontFamily:'inherit',minWidth:260,
              }}
            />
            <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
              ⟳ 새로고침
            </button>
          </div>
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            아카이브를 불러오지 못했어요: {error}
          </div>
        )}

        <div className="admin-table-wrap">
          <div className="queue-row head">
            <div>#</div>
            <div>아이</div>
            <div>보호자</div>
            <div>증상</div>
            <div>영상</div>
            <div>발행일</div>
            <div></div>
          </div>
          {loading ? (
            <div className="queue-row">
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'40px 0'}}>
                <div className="spinner" style={{display:'inline-block'}}/>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="queue-row">
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'40px 0',color:'#718096',fontSize:13}}>
                {debouncedSearch
                  ? `"${debouncedSearch}"에 해당하는 발행 리포트가 없어요.`
                  : "아직 발행된 리포트가 없어요."}
              </div>
            </div>
          ) : (
            rows.map((c) => {
              const age = formatAge(c.child?.birth_month);
              const concern = c.concern_tags?.[0] ?? "(증상 미입력)";
              return (
                <div key={c.id} className="queue-row" style={{cursor:'pointer'}}
                  onClick={() => { window.location.hash = `#admin/archive/${c.id}`; }}>
                  <div style={{fontSize:11,color:'#718096',fontVariantNumeric:'tabular-nums'}}>{shortId(c.id)}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13}}>
                      {c.child?.name ?? "(이름 없음)"}
                      {age && <span style={{color:'#718096',fontWeight:500}}> · {age}</span>}
                    </div>
                  </div>
                  <div style={{fontSize:12,color:'#4a5568',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.parent?.display_name ? `${c.parent.display_name} · ` : ""}
                    {c.parent?.email ?? "-"}
                  </div>
                  <div style={{color:'#4a5568'}}>{concern}</div>
                  <div style={{fontWeight:700,fontVariantNumeric:'tabular-nums'}}>🎬 {c.video_count}</div>
                  <div style={{color:'#718096',fontVariantNumeric:'tabular-nums'}}>{formatDate(c.sent_at)}</div>
                  <div style={{color:'#2c5f8d',fontSize:14}}>→</div>
                </div>
              );
            })
          )}
        </div>

        {(hasPrev || hasNext) && (
          <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:16}}>
            <button
              className="btn btn-outline"
              style={{padding:'8px 16px',fontSize:12,opacity: hasPrev ? 1 : 0.4}}
              disabled={!hasPrev}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← 이전
            </button>
            <div style={{padding:'8px 14px',fontSize:12,color:'#718096'}}>
              {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} / {total}
            </div>
            <button
              className="btn btn-outline"
              style={{padding:'8px 16px',fontSize:12,opacity: hasNext ? 1 : 0.4}}
              disabled={!hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              다음 →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
